import { useState, useEffect } from "react";
import axiosInstance from "@/config/axios";
import ReactMarkdown from "react-markdown";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { MessageCircle, Mail, User, RefreshCw, Database, Search, Shield, AlertTriangle, CheckCircle, XCircle, Eye, ThumbsUp, ThumbsDown, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get country flag emoji
const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode === 'XX' || countryCode === 'LC') return '🌍';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

// Tailwind version of Bootstrap's table-responsive
function TableResponsive({ children }) {
  return (
    <div className="w-full overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
      {children}
    </div>
  );
}

export default function ChatModeration() {
  const [conversations, setConversations] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [loading, setLoading] = useState(false);
  const [globalRateLimitEnabled, setGlobalRateLimitEnabled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [chatErrors, setChatErrors] = useState([]);
  const [loadingErrors, setLoadingErrors] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [errorPagination, setErrorPagination] = useState({
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [currentErrorPage, setCurrentErrorPage] = useState(1);
  const [loadingMoreErrors, setLoadingMoreErrors] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState({});

  // Fetch conversations from backend
  const fetchConversations = async (page = 1, append = false, search = "") => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const token = localStorage.getItem("token");
      const params = { page, page_size: 20 };
      if (search && search.trim()) {
        params.search = search.trim();
      }

      const response = await axiosInstance.get(`${API}/ai/chat/all-conversations`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      if (append) {
        setConversations((prev) => [...prev, ...(response.data.conversations || [])]);
      } else {
        setConversations(response.data.conversations || []);
      }

      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
      setCurrentPage(page);
    } catch (error) {
      console.error("Failed to fetch conversations", error);
      toast.error("Failed to fetch conversations");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const invalidateCache = async () => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.post(
        `${API}/ai/chat/invalidate-cache`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Cache cleared! Refreshing data...");
      setConversations([]);
      setCurrentPage(1);
      fetchConversations(1, false, searchQuery);
    } catch (error) {
      console.error("Failed to invalidate cache", error);
      toast.error("Failed to invalidate cache");
    }
  };

  const fetchGlobalRateLimit = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/admin/settings/ip-rate-limit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGlobalRateLimitEnabled(response.data.global_ip_rate_limit_enabled || false);
    } catch (error) {
      console.error("Failed to fetch rate limit settings", error);
    }
  };

  const updateGlobalRateLimit = async (enabled) => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.post(
        `${API}/admin/settings/ip-rate-limit?enabled=${enabled}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGlobalRateLimitEnabled(enabled);
      toast.success(`Global IP rate limiting ${enabled ? "enabled" : "disabled"}`);
    } catch (error) {
      console.error("Failed to update rate limiting", error);
      toast.error("Failed to update rate limiting");
    }
  };

  const fetchChatErrors = async (page = 1, append = false) => {
    try {
      if (append) {
        setLoadingMoreErrors(true);
      } else {
        setLoadingErrors(true);
      }
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/admin/chat-errors?page=${page}&page_size=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (append) {
        setChatErrors(prev => [...prev, ...(response.data.errors || [])]);
      } else {
        setChatErrors(response.data.errors || []);
      }
      
      if (response.data.pagination) {
        setErrorPagination(response.data.pagination);
        setCurrentErrorPage(page);
      }
    } catch (error) {
      console.error("Failed to fetch chat errors", error);
    } finally {
      setLoadingErrors(false);
      setLoadingMoreErrors(false);
    }
  };

  const loadMoreErrors = () => {
    if (errorPagination.has_next && !loadingMoreErrors) {
      fetchChatErrors(currentErrorPage + 1, true);
    }
  };

  const resolveError = async (errorId) => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.patch(
        `${API}/admin/chat-errors/${errorId}/resolve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Error marked as resolved");
      // Refresh errors from current page
      fetchChatErrors(currentErrorPage, false);
    } catch (error) {
      console.error("Failed to resolve error", error);
      toast.error("Failed to resolve error");
    }
  };

  // Format: 16/12/2025 23.49.56
  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}.${pad(d.getMinutes())}.${pad(d.getSeconds())}`;
  };

  const formatRelativeTime = (iso) => {
    if (!iso) return "-";
    const target = new Date(iso);
    if (Number.isNaN(target.getTime())) return "-";
    const diffMs = Date.now() - target.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return "just now";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    const years = Math.floor(months / 12);
    return `${years}y ago`;
  };
  const viewErrorDetails = (error) => {
    setSelectedError(error);
    setErrorDialogOpen(true);
  };

  const handleAdminFeedback = async (message, feedbackType) => {
    const messageKey = message.id || message.timestamp;
    
    // Check if message has required fields
    if (!message.id) {
      toast.error("Cannot submit feedback: Message ID is missing");
      console.error("Message object:", message);
      return;
    }
    
    if (!selectedConversation?.session_id) {
      toast.error("Cannot submit feedback: Session ID is missing");
      return;
    }
    
    if (!selectedConversation?.actor_id) {
      toast.error("Cannot submit feedback: Actor ID is missing");
      return;
    }
    
    // Check if feedback already exists for this message and type
    const hasExistingFeedback = feedbackType === 'positive' 
      ? message.feedback?.admin_positive 
      : message.feedback?.admin_negative;
    
    try {
      setSubmittingFeedback(prev => ({...prev, [messageKey]: feedbackType}));
      
      const token = localStorage.getItem("token");
      const response = await axiosInstance.post(
        `${API}/public/chat/feedback`,
        {
          session_id: selectedConversation.session_id,
          message_id: message.id,
          actor_id: selectedConversation.actor_id,
          feedback_type: feedbackType,
          feedback_comment: `Admin review: ${feedbackType === 'positive' ? 'Good response' : 'Needs improvement'}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show appropriate toast message based on action
      const action = response.data?.action || 'added';
      if (action === 'removed') {
        toast.success(`Admin feedback removed: ${feedbackType === 'positive' ? 'Thumbs Up' : 'Thumbs Down'} removed`);
      } else {
        toast.success(`Admin feedback submitted: ${feedbackType === 'positive' ? 'Thumbs Up' : 'Thumbs Down'}`);
      }
      
      // Refresh messages to show updated feedback highlights
      if (selectedConversation?.session_id) {
        try {
          const token = localStorage.getItem("token");
          const response = await axiosInstance.get(
            `${API}/ai/chat/conversation/${selectedConversation.session_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          // Update the conversation with refreshed messages (including feedback info)
          setSelectedConversation(prev => ({
            ...prev,
            messages: response.data.messages
          }));
        } catch (error) {
          console.error("Failed to refresh messages after feedback:", error);
        }
      }
      
      // Keep the feedback state for visual indication
      setTimeout(() => {
        setSubmittingFeedback(prev => {
          const newState = {...prev};
          delete newState[messageKey];
          return newState;
        });
      }, 2000);
      
    } catch (error) {
      console.error("Feedback submission error:", error);
      console.error("Error response:", error.response?.data);
      console.error("Request payload:", {
        session_id: selectedConversation.session_id,
        message_id: message.id,
        actor_id: selectedConversation.actor_id,
        feedback_type: feedbackType
      });
      
      const errorMsg = error.response?.data?.detail || "Failed to submit feedback";
      toast.error(errorMsg);
      
      setSubmittingFeedback(prev => {
        const newState = {...prev};
        delete newState[messageKey];
        return newState;
      });
    }
  };

  const viewConversation = async (conversation) => {
    setDialogOpen(true);
    setSelectedConversation(conversation);
    
    // If messages are empty, fetch them
    if (!conversation.messages || conversation.messages.length === 0) {
      try {
        const token = localStorage.getItem("token");
        const response = await axiosInstance.get(
          `${API}/ai/chat/conversation/${conversation.session_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Update the conversation with messages
        setSelectedConversation({
          ...conversation,
          messages: response.data.messages
        });
      } catch (error) {
        console.error("Failed to fetch conversation details", error);
        toast.error("Failed to load conversation messages");
      }
    }
  };

  // Ensure horizontal scroll is only inside explicit overflow-x-auto containers (like TableResponsive),
  // not on the whole page
  useEffect(() => {
    const prevBodyOverflowX = document.body.style.overflowX;
    const prevHtmlOverflowX = document.documentElement.style.overflowX;

    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflowX = "hidden";

    return () => {
      document.body.style.overflowX = prevBodyOverflowX;
      document.documentElement.style.overflowX = prevHtmlOverflowX;
    };
  }, []);

  // Load more (pagination) - optional for later
  const loadMoreConversations = () => {
    if (pagination.has_next && !loadingMore) {
      fetchConversations(currentPage + 1, true, searchQuery);
    }
  };

  // Initial load
  useEffect(() => {
    fetchConversations(1);
    fetchGlobalRateLimit();
    fetchChatErrors();
  }, []);

  // Debounced server-side search
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchConversations(1, false, searchQuery);
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  return (
    <div className="space-y-6 p-6">
      {/* Header + Actions */}
      <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2 sm:gap-3">
            <MessageCircle className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600" />
            <span className="truncate">Chat Moderation</span>
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">
            View and monitor all conversations between users and AI Actors
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            onClick={() => {
              setConversations([]);
              fetchConversations(1);
            }}
            variant="outline"
            disabled={loading}
            className="flex items-center gap-1 sm:gap-2 flex-1 sm:flex-initial text-xs sm:text-sm"
          >
            <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button
            onClick={invalidateCache}
            variant="outline"
            disabled={loading}
            className="flex items-center gap-1 sm:gap-2 flex-1 sm:flex-initial text-xs sm:text-sm"
            title="Clear count cache and get fresh data"
          >
            <Database className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Clear Cache</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards (Counter / Analytics) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Total Conversations</p>
                <p className="text-xl sm:text-2xl font-bold truncate">
                  {(pagination.total || 0).toLocaleString()}
                </p>
                {pagination.total > conversations.length && (
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    Showing {conversations.length.toLocaleString()} of{" "}
                    {pagination.total.toLocaleString()}
                  </p>
                )}
              </div>
              <MessageCircle className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Total Messages (Loaded)</p>
                <p className="text-xl sm:text-2xl font-bold truncate">
                  {conversations
                    .reduce((sum, conv) => sum + (conv.message_count || 0), 0)
                    .toLocaleString()}
                </p>
                {conversations.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    Across {conversations.length} conversations
                  </p>
                )}
              </div>
              <Mail className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm text-muted-foreground">Unique Users (Loaded)</p>
                <p className="text-xl sm:text-2xl font-bold truncate">
                  {(() => {
                    const emails = conversations
                      .map((c) => c.user_email || c.email)
                      .filter((email) => email != null && email !== "")
                      .map((email) => email.toLowerCase().trim());
                    const uniqueEmails = new Set(emails);
                    return uniqueEmails.size;
                  })()}
                </p>
                {conversations.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    From {conversations.length} conversations
                  </p>
                )}
              </div>
              <User className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 sm:w-5 sm:h-5" />
        <Input
          placeholder="Search by user name, email, or AI Actor..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 sm:pl-10 pr-10 text-sm sm:text-base w-full"
        />
        {loading && searchQuery && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </div>

      {/* Global IP Rate Limiting Card */}
      <Card>
        <CardHeader className="p-4 md:p-6 w-full">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
            Global IP Rate Limiting
          </CardTitle>
          <p className="text-xs sm:text-sm text-muted-foreground mt-2">
            When enabled, limits each IP address to a total of 10 messages across ALL AI Actors combined.
          </p>
        </CardHeader>
        <CardContent className="p-4 md:p-6 w-full">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 p-3 sm:p-4 border rounded-lg hover:bg-slate-50 transition-colors w-full">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm sm:text-base text-slate-900">IP Rate Limiting</div>
              <div className="text-xs sm:text-sm text-muted-foreground mt-1">
                Limit: <span className="font-semibold text-indigo-600">10 total messages per IP</span> across all actors
              </div>
              {globalRateLimitEnabled && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 mt-2 text-xs">
                  Active - Users limited to 10 messages total
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 sm:gap-3 self-end sm:self-auto">
              <Label htmlFor="global-rate-limit" className="text-xs sm:text-sm font-medium">
                {globalRateLimitEnabled ? "Enabled" : "Disabled"}
              </Label>
              <Switch
                id="global-rate-limit"
                checked={globalRateLimitEnabled}
                onCheckedChange={(checked) => updateGlobalRateLimit(checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Placeholder content to see layout only */}
      <Card>
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            Header, analytics cards, search bar, Global IP Rate Limiting, and Chat Error Logs are mounted. Testing responsiveness and horizontal scroll.
          </p>
        </CardContent>
      </Card>

      {/* Chat Error Logs */}
      <Card className="border-2 border-red-200">
        <CardHeader className="p-4 md:p-6 w-full">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            Chat Error Logs
          </CardTitle>
          <p className="text-xs sm:text-sm text-muted-foreground mt-2">
            Failed chat attempts where both primary and backup models failed
          </p>
        </CardHeader>
        <CardContent className="p-4 md:p-6 w-full">
          {loadingErrors ? (
            <p className="text-xs sm:text-sm text-muted-foreground text-center py-4">
              Loading errors...
            </p>
          ) : chatErrors.length === 0 ? (
            <div className="p-3 sm:p-4 border rounded-lg bg-green-50 text-center">
              <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 mx-auto mb-2" />
              <p className="text-xs sm:text-sm text-green-700 font-medium">
                No errors logged! 🎉
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                All chats are working smoothly
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {chatErrors.map((error) => (
                <div
                  key={error.id}
                  className={`flex flex-col sm:flex-row items-start justify-between gap-2 sm:gap-3 p-2 sm:p-3 border rounded-lg ${
                    error.resolved ? "bg-gray-50 opacity-60" : "bg-red-50"
                  }`}
                >
                  <div className="flex-1 min-w-0 w-full">
                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                      {error.resolved ? (
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-3 h-3 sm:w-4 sm:h-4 text-red-600 flex-shrink-0" />
                      )}
                      <span className="font-semibold text-xs sm:text-sm truncate">
                        {error.actor_name}
                      </span>
                      <Badge variant="outline" className="text-xs flex-shrink-0">
                        {error.error_type}
                      </Badge>
                      <span className="text-xs text-muted-foreground hidden sm:inline">
                        {formatDate(error.timestamp)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      User: {error.user_name} ({error.user_email})
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      Primary: {error.primary_model} | Backup: {error.backup_model || "None"}
                    </p>
                    <span className="text-xs text-muted-foreground sm:hidden mt-1 block">
                      {formatDate(error.timestamp)}
                    </span>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => viewErrorDetails(error)}
                      className="flex-1 sm:flex-initial text-xs"
                    >
                      <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      Details
                    </Button>
                    {!error.resolved && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => resolveError(error.id)}
                        className="text-green-600 hover:text-green-700 flex-1 sm:flex-initial text-xs"
                      >
                        Resolve
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {errorPagination.has_next && (
                <div className="flex justify-center mt-4">
                  <Button
                    onClick={loadMoreErrors}
                    disabled={loadingMoreErrors}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    {loadingMoreErrors ? "Loading..." : `Load More (${errorPagination.total - chatErrors.length} remaining)`}
                  </Button>
                </div>
              )}
              {chatErrors.length > 0 && !errorPagination.has_next && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Showing all {chatErrors.length} of {errorPagination.total} errors
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversations List */}
      {conversations.length === 0 ? (
        <Card>
          <CardContent className="p-6 sm:p-8 text-center">
            <MessageCircle className="w-10 h-10 sm:w-12 sm:h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-base sm:text-lg font-medium text-muted-foreground">
              {searchQuery ? "No conversations found" : "No conversations yet"}
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground mt-2">
              {searchQuery
                ? "Try adjusting your search"
                : "Conversations will appear here once users start chatting with AI Actors"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile Cards */}
          <div className="block lg:hidden space-y-3">
            {conversations.map((conversation) => (
              <Card key={conversation.session_id}>
                <CardContent className="p-3 sm:p-4">
                  <div className="space-y-2 sm:space-y-3">
                    {/* Header Row */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-col gap-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs">
                            {conversation.actor_name || "Unknown Actor"}
                          </Badge>
                          <Badge
                            variant="outline"
                            className="text-xs"
                          >
                            {conversation.type === "call" ? "Call" : "Chat"}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {conversation.user_name || "Unknown User"}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {conversation.user_email || "No email"}
                        </p>
                      </div>
                    </div>

                    {/* Meta info */}
                    <div className="space-y-1 text-xs text-muted-foreground">
                      <p>
                        Started:{" "}
                        <span className="font-medium">
                          {conversation.created_at ? formatDate(conversation.created_at) : "-"}
                        </span>
                      </p>
                      <p>
                        Messages:{" "}
                        <span className="font-medium">{conversation.message_count || 0}</span>
                      </p>
                    </div>

                    {/* Feedback counts */}
                    {(conversation.feedback?.user_positive > 0 || 
                      conversation.feedback?.user_negative > 0 || 
                      conversation.feedback?.admin_positive > 0 || 
                      conversation.feedback?.admin_negative > 0) && (
                      <div className="flex items-center gap-3 pt-2 border-t">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">User:</span>
                          <div className="flex items-center gap-1 text-green-600">
                            <ThumbsUp className="w-3 h-3" />
                            <span className="text-xs font-medium">
                              {conversation.feedback?.user_positive || 0}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-red-600">
                            <ThumbsDown className="w-3 h-3" />
                            <span className="text-xs font-medium">
                              {conversation.feedback?.user_negative || 0}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Admin:</span>
                          <div className="flex items-center gap-1 text-blue-600">
                            <ThumbsUp className="w-3 h-3" />
                            <span className="text-xs font-medium">
                              {conversation.feedback?.admin_positive || 0}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-orange-600">
                            <ThumbsDown className="w-3 h-3" />
                            <span className="text-xs font-medium">
                              {conversation.feedback?.admin_negative || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop Table - wrapped in Tailwind 'table-responsive' helper */}
          <Card className="hidden lg:block">
            <CardContent className="p-0">
              <TableResponsive>
                <table className="w-full table-auto">
                  <thead className="bg-slate-50 border-b text-xs">
                    <tr>
                      <th className="px-3 xl:px-4 py-2 text-left font-medium text-slate-500 uppercase tracking-wider">
                        AI Actor
                      </th>
                      <th className="px-3 xl:px-4 py-2 text-left font-medium text-slate-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-3 xl:px-4 py-2 text-left font-medium text-slate-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-3 xl:px-4 py-2 text-left font-medium text-slate-500 uppercase tracking-wider">
                        Country
                       </th>
                      <th className="px-3 xl:px-4 py-2 text-left font-medium text-slate-500 uppercase tracking-wider">
                        Feedback
                      </th>
                      <th className="px-3 xl:px-4 py-2 text-left font-medium text-slate-500 uppercase tracking-wider">
                        Started
                      </th>
                      <th className="px-3 xl:px-4 py-2 text-left font-medium text-slate-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200 text-xs">
                    {conversations.map((conversation) => (
                      <tr key={conversation.session_id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-3 xl:px-4 py-3 align-top">
                          <div className="flex flex-col gap-1">
                            <Badge
                              variant="outline"
                              className="px-1.5 py-0.5 text-[11px] bg-indigo-50 text-indigo-700 border-indigo-200 max-w-[180px] truncate"
                            >
                              {conversation.actor_name || "Unknown Actor"}
                            </Badge>
                          </div>
                        </td>
                        <td className="px-3 xl:px-4 py-3 align-top">
                          <div className="flex flex-col gap-1">
                            <Badge
                              variant="outline"
                              className={`px-1.5 py-0.5 text-[11px] ${
                                conversation.type === "call"
                                  ? "bg-green-50 text-green-700 border-green-200"
                                  : "bg-blue-50 text-blue-700 border-blue-200"
                              }`}
                            >
                              {conversation.type === "call" ? "📞 Call" : "💬 Chat"}
                            </Badge>
                            <div className="text-[11px] text-slate-700">
                              {conversation.message_count || 0} msgs
                              {conversation.type === "call" && conversation.duration && (
                                <> | {conversation.duration}</>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 xl:px-4 py-3 align-top">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2 text-slate-900 break-words">
                              <User className="w-3 h-3 text-slate-400 flex-shrink-0" />
                              <span className="text-[11px] sm:text-xs font-medium">
                                {conversation.user_name || "Unknown User"}
                              </span>
                            </div>
                            {(() => {
                              if (conversation.type === "call") {
                                // For Call: Show email
                                const email = conversation.user_email || conversation.email;
                                if (email) {
                                  return (
                                    <div className="flex items-center gap-2 text-slate-600 break-words">
                                      <span className="text-sm">✉️</span>
                                      <span className="text-[11px] sm:text-xs">
                                        {email}
                                      </span>
                                    </div>
                                  );
                                }
                                return (
                                  <span className="text-[11px] text-slate-400 italic">
                                    No email
                                  </span>
                                );
                              } else {
                                // For Chat: Show phone
                                const phone =
                                  conversation.phone_number ||
                                  conversation.user_phone ||
                                  conversation.phone ||
                                  conversation.user_email; // Fallback to email if phone not available
                                if (phone) {
                                  return (
                                    <div className="flex items-center gap-2 text-slate-600 break-words">
                                      <span className="text-sm">📞</span>
                                      <span className="text-[11px] sm:text-xs">
                                        {phone}
                                      </span>
                                    </div>
                                  );
                                }
                                return (
                                  <span className="text-[11px] text-slate-400 italic">
                                    No contact
                                  </span>
                                );
                              }
                            })()}
                          </div>
                        </td>
                        <td className="px-3 xl:px-4 py-3 align-top">
                          {typeof conversation.ip_address === "string" &&
                            conversation.ip_address.toLowerCase().includes("not tracked") ? (
                            // Explicitly marked as not tracked: show only this text, no icons
                            <div className="text-slate-400 italic">Not tracked</div>
                          ) : (!conversation.city || conversation.city === "Unknown") &&
                            (!conversation.ip_address ||
                              conversation.ip_address === "Unknown (Legacy)" ||
                              conversation.ip_address === "127.0.0.1") ? (
                            // Local / unknown location
                            <div className="flex items-start gap-2">
                              <span className="text-lg">🌍</span>
                              <div className="min-w-0">
                                <div className="text-slate-900 break-words">Local</div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start gap-2">
                              <span className="text-lg">
                                {getCountryFlag(conversation.country_code)}
                              </span>
                              <div className="min-w-0">
                                <div className="text-slate-900 break-words">
                                  {conversation.country && conversation.country !== "Unknown"
                                    ? conversation.country
                                    : <span className="italic text-slate-400">Not tracked</span>}
                                </div>
                                {(conversation.city || (conversation.ip_address && conversation.ip_address !== "Unknown (Legacy)")) && (
                                  <div className="text-[11px] text-slate-500 break-words">
                                    {conversation.city || "-"}
                                    {conversation.ip_address && conversation.ip_address !== "Unknown (Legacy)" && (
                                      <> ({conversation.ip_address})</>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-3 xl:px-4 py-3 align-top">
                          <div className="flex flex-col gap-2">
                            <div className="flex flex-col gap-1">
                              <span className="text-[10px] text-muted-foreground">User Feedback:</span>
                              <div className="flex items-center gap-2 text-[11px]">
                                <div className="flex items-center gap-1 text-green-600">
                                  <ThumbsUp className="w-3 h-3" />
                                  <span className="font-medium">
                                    {conversation.feedback?.user_positive || 0}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1 text-red-600">
                                  <ThumbsDown className="w-3 h-3" />
                                  <span className="font-medium">
                                    {conversation.feedback?.user_negative || 0}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="text-[10px] text-muted-foreground">Admin Feedback:</span>
                              <div className="flex items-center gap-2 text-[11px]">
                                <div className="flex items-center gap-1 text-blue-600">
                                  <ThumbsUp className="w-3 h-3" />
                                  <span className="font-medium">
                                    {conversation.feedback?.admin_positive || 0}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1 text-orange-600">
                                  <ThumbsDown className="w-3 h-3" />
                                  <span className="font-medium">
                                    {conversation.feedback?.admin_negative || 0}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 xl:px-4 py-3 align-top">
                          <div className="text-slate-700 break-words">
                            {conversation.created_at ? formatDate(conversation.created_at) : "-"}
                          </div>
                          {conversation.last_message_at && (
                            <div className="text-[11px] text-slate-500">
                              last activity : {formatRelativeTime(conversation.last_message_at)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 xl:px-4 py-3 align-top">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewConversation(conversation)}
                            className="text-[11px] px-2 py-1"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableResponsive>
            </CardContent>
          </Card>

          {/* Load More Button */}
          {pagination.has_next && !searchQuery && (
            <div className="flex justify-center px-2 sm:px-3 md:px-0 mt-4">
              <Button
                onClick={loadMoreConversations}
                disabled={loadingMore}
                variant="outline"
                className="w-full sm:max-w-md text-xs sm:text-sm md:text-base py-2 sm:py-3"
              >
                {loadingMore ? (
                  <>
                    <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">
                      Load More ({conversations.length} of {pagination.total})
                    </span>
                    <span className="sm:hidden">
                      More ({conversations.length}/{pagination.total})
                    </span>
                  </>
                )}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Conversation Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl md:max-w-4xl max-h-[85vh] sm:max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Conversation Details</DialogTitle>
          </DialogHeader>

          {selectedConversation && (
            <div className="space-y-4 sm:space-y-6 mt-3 sm:mt-4">
              {/* Conversation Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 p-3 sm:p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Type</p>
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${selectedConversation.type === 'call' 
                      ? 'bg-green-50 text-green-700 border-green-200' 
                      : 'bg-blue-50 text-blue-700 border-blue-200'
                    }`}
                  >
                    {selectedConversation.type === 'call' ? '📞 Call' : '💬 Chat'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">AI Actor</p>
                  <p className="text-sm sm:text-base font-medium truncate">{selectedConversation.actor_name}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">User</p>
                  <p className="text-sm sm:text-base font-medium truncate">{selectedConversation.user_name}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Email</p>
                  <p className="text-sm sm:text-base font-medium truncate">{selectedConversation.user_email}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Messages</p>
                  <p className="text-sm sm:text-base font-medium">{selectedConversation.message_count}</p>
                </div>
                {selectedConversation.type === 'call' && selectedConversation.duration && (
                  <div>
                    <p className="text-xs sm:text-sm text-muted-foreground">Duration</p>
                    <p className="text-sm sm:text-base font-medium">⏱️ {selectedConversation.duration}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Started</p>
                  <p className="text-xs sm:text-sm font-medium">{formatDate(selectedConversation.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Last Message</p>
                  <p className="text-xs sm:text-sm font-medium">{formatDate(selectedConversation.last_message_at)}</p>
                </div>
              </div>

              {/* Messages */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
                  {selectedConversation.type === 'call' ? 'Call Transcript' : 'Chat History'}
                </h3>
                <div className="space-y-3 sm:space-y-4 max-h-[50vh] sm:max-h-96 overflow-y-auto border rounded-lg p-3 sm:p-4 bg-slate-50">
                  {!selectedConversation.messages || selectedConversation.messages.length === 0 ? (
                    <div className="flex items-center justify-center py-6 sm:py-8">
                      <div className="text-center">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        <p className="text-xs sm:text-sm text-muted-foreground">Loading messages...</p>
                      </div>
                    </div>
                  ) : selectedConversation.messages.map((msg, index) => (
                    <div key={msg.id || index} className="space-y-2 sm:space-y-3">
                      {/* User Message */}
                      {msg.user_message && (
                        <div className="flex justify-end">
                          <div className="max-w-[85%] sm:max-w-[70%] rounded-lg px-3 sm:px-4 py-2 bg-slate-900 text-white">
                            <p className="text-xs text-slate-300 mb-1 truncate">
                              {selectedConversation.user_name}
                            </p>
                            <div className="text-xs sm:text-sm prose prose-sm prose-invert max-w-none break-words">
                              <ReactMarkdown>{msg.user_message}</ReactMarkdown>
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(msg.timestamp).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* AI Assistant Response */}
                      {msg.assistant_message && (
                        <div className="flex justify-start">
                          <div className={`max-w-[85%] sm:max-w-[70%] rounded-lg px-3 sm:px-4 py-2 bg-white border-2 ${
                            msg.feedback?.admin_positive || msg.feedback?.user_positive
                              ? 'border-green-400 bg-green-50'
                              : msg.feedback?.admin_negative || msg.feedback?.user_negative
                              ? 'border-red-400 bg-red-50'
                              : 'border-slate-200'
                          }`}>
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs text-muted-foreground truncate">
                                {selectedConversation.actor_name}
                              </p>
                              {/* Feedback indicators */}
                              {(msg.feedback?.user_positive || msg.feedback?.user_negative || 
                                msg.feedback?.admin_positive || msg.feedback?.admin_negative) && (
                                <div className="flex items-center gap-1 ml-2">
                                  {msg.feedback?.user_positive && (
                                    <div className="flex items-center gap-0.5" title="User liked this">
                                      <ThumbsUp className="w-3 h-3 text-green-600" />
                                    </div>
                                  )}
                                  {msg.feedback?.user_negative && (
                                    <div className="flex items-center gap-0.5" title="User disliked this">
                                      <ThumbsDown className="w-3 h-3 text-red-600" />
                                    </div>
                                  )}
                                  {msg.feedback?.admin_positive && (
                                    <div className="flex items-center gap-0.5" title="Admin liked this">
                                      <ThumbsUp className="w-3 h-3 text-blue-600" />
                                    </div>
                                  )}
                                  {msg.feedback?.admin_negative && (
                                    <div className="flex items-center gap-0.5" title="Admin disliked this">
                                      <ThumbsDown className="w-3 h-3 text-orange-600" />
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="text-xs sm:text-sm prose prose-sm max-w-none break-words">
                              <ReactMarkdown>{msg.assistant_message}</ReactMarkdown>
                            </div>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mt-2">
                              <p className="text-xs text-muted-foreground">
                                {new Date(msg.timestamp).toLocaleTimeString()}
                              </p>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`h-7 px-2 ${
                                    submittingFeedback[msg.id || msg.timestamp] === 'positive' || msg.feedback?.admin_positive
                                      ? 'text-green-600 bg-green-50'
                                      : 'text-gray-500 hover:text-green-600 hover:bg-green-50'
                                  }`}
                                  onClick={() => handleAdminFeedback(msg, 'positive')}
                                  disabled={!!submittingFeedback[msg.id || msg.timestamp]}
                                >
                                  <ThumbsUp className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`h-7 px-2 ${
                                    submittingFeedback[msg.id || msg.timestamp] === 'negative' || msg.feedback?.admin_negative
                                      ? 'text-red-600 bg-red-50'
                                      : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
                                  }`}
                                  onClick={() => handleAdminFeedback(msg, 'negative')}
                                  disabled={!!submittingFeedback[msg.id || msg.timestamp]}
                                >
                                  <ThumbsDown className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Session Info */}
              <div className="text-xs text-muted-foreground border-t pt-4">
                <p>Session ID: {selectedConversation.session_id}</p>
                <p>Actor ID: {selectedConversation.actor_id}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Error Details Dialog */}
      <Dialog open={errorDialogOpen} onOpenChange={setErrorDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Chat Error Details
            </DialogTitle>
          </DialogHeader>

          {selectedError && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">AI Actor</p>
                  <p className="font-semibold">{selectedError.actor_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Error Type</p>
                  <Badge variant="destructive">{selectedError.error_type}</Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">User</p>
                  <p className="text-sm">{selectedError.user_name} ({selectedError.user_email})</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Timestamp</p>
                  <p className="text-sm">{formatDate(selectedError.timestamp)}</p>
                </div>
              </div>

              {/* User Message */}
              <div>
                <p className="text-sm font-semibold mb-2">User's Message:</p>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm break-words">{selectedError.user_message || "No message"}</p>
                </div>
              </div>

              {/* Primary Model Error */}
              <div>
                <p className="text-sm font-semibold mb-2">Primary Model: {selectedError.primary_model}</p>
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-xs font-mono whitespace-pre-wrap break-words">{selectedError.primary_error}</p>
                </div>
              </div>

              {/* Backup Model Error */}
              {selectedError.backup_model && (
                <div>
                  <p className="text-sm font-semibold mb-2">Backup Model: {selectedError.backup_model}</p>
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded">
                    {selectedError.backup_error ? (
                      <p className="text-xs font-mono whitespace-pre-wrap break-words">{selectedError.backup_error}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">No backup error (backup not attempted or succeeded)</p>
                    )}
                  </div>
                </div>
              )}

              {!selectedError.backup_model && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">⚠️ No backup model configured for this AI Actor</p>
                </div>
              )}

              {/* Session Info */}
              <div className="text-xs text-muted-foreground border-t pt-4">
                <p>Session ID: {selectedError.session_id}</p>
                <p>Actor ID: {selectedError.actor_id}</p>
                <p>Error ID: {selectedError.id}</p>
                <p>Resolved: {selectedError.resolved ? "Yes" : "No"}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
